import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, Output } from '@angular/core';

import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { TranslateService } from '@ngx-translate/core';

import { ANIMATION_SLIDE_BOTTOM_COMPONENT } from '@src/app/utils/animation-components';
import { RouteLineComponent } from './route-line/route-line.component';
import { RouteSummaryDataComponent } from './route-summary-data/route-summary-data.component';
import { Vehicle } from '@commons/models/domain/Vehicle';
import { SelectedRouteReportSummary } from '@src/app/interfaces/route-stats.interface';
import { DistancePipe } from '@commons/pipes/distance.pipe';
import { CalculateDriveTimeRoutePipe } from '@shared/pipes/calculate-time-between-dates.pipe';
import { formatMinutesToHours } from '@src/app/utils/date';
import { IGroupObjRouteEventReport, IRouteEventReportData } from '@src/app/interfaces/report-route.interface';
import { Driver } from '@commons/models/domain/Driver';
import { NewDataStoreService } from '@shared/services/new-data-store.service';

enum DirectionSwitch {
  Previous = 1,
  Next = 2,
}

@Component({
  selector: 'app-route-details-modal',
  standalone: true,
  imports: [CommonModule, RouteLineComponent, RouteSummaryDataComponent, ProgressSpinnerModule],
  providers: [DistancePipe, CalculateDriveTimeRoutePipe],
  templateUrl: './route-details-modal.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrls: ['./route-details-modal.component.scss'],
  animations: ANIMATION_SLIDE_BOTTOM_COMPONENT,
})
export class RouteDetailsModalComponent {
  @Output() private nextRoute$: EventEmitter<void> = new EventEmitter();
  @Output() private prevRoute$: EventEmitter<void> = new EventEmitter();

  @Input() public isVisible: boolean;
  @Input() public vehicle: Vehicle;
  @Input() public groupRoutes: IGroupObjRouteEventReport;

  @Input('routeData') public set routeData(data: IRouteEventReportData) {
    this._routeData = data;
    setTimeout(() => {
      if (this._routeData) this.initData();
    }, 0);
  }

  public routeSummary: SelectedRouteReportSummary[] = [];
  public _routeData: IRouteEventReportData;
  public isLoadingRoute: boolean;
  public driverData: Driver;

  public readonly DirectionSwitch = DirectionSwitch;

  constructor(
    private distancePipe: DistancePipe,
    private CalculateDriveTimeRoutePipe: CalculateDriveTimeRoutePipe,
    private changeDetRef: ChangeDetectorRef,
    private translate: TranslateService,
    private newDataStoreService: NewDataStoreService,
  ) {}

  private initData(): void {
    this.setRouteSummary();
    this.setDriverData();
    this.changeDetRef.detectChanges();
  }

  private setRouteSummary(): void {
    const { stopTimeInMinutes = null, } = this._routeData;

    this.routeSummary = [
      {title: this.translate.instant('reports.distance'), value: this.distancePipe.transform(this._routeData.D.D), icon: 'pi pi-map-marker' },
      {title: this.translate.instant('reports.time'), value: this.CalculateDriveTimeRoutePipe.transform(this._routeData), icon: 'pi pi-angle-up' },
      {title: this.translate.instant('reports.stop'), value: stopTimeInMinutes ? formatMinutesToHours(stopTimeInMinutes) : this.translate.instant('labels.empty'), icon: 'pi pi-flag' },
    ];
  }

  public onClickSwitchBtn(direction: DirectionSwitch): void {
    direction === DirectionSwitch.Next ? this.nextRoute$.emit() : this.prevRoute$.next();
    this.isLoadingRoute = true;
    this.changeDetRef.detectChanges();

    setTimeout(() => {
      this.isLoadingRoute = false;
      this.changeDetRef.detectChanges();
    }, 400);
  }

  private setDriverData(): void {
    if (this._routeData.Dr) {
      this.driverData = this.newDataStoreService.getDriverById(this._routeData.Dr);
      return;
    }
    this.driverData = null!;
  }

}
